/**
 * The one-time product-update opt-in. Asked ONCE per account, on the next visit
 * after this ships - existing users included, not only new signups.
 *
 * Consent itself is written by the same authed RPC the /account toggle uses
 * (`setMySubscription`), so there is no second confirmation email and no new
 * SQL: the session already proves the address belongs to the person answering.
 *
 * This module is only the DECISION - may we ask, and have we asked already -
 * kept out of the component so it can be tested without a DOM.
 *
 * The record is local, and that is forced rather than lazy:
 *  - `set_my_email_subscription(false)` only UPDATEs an existing row, so a
 *    decline writes NOTHING and `my_email_subscription()` still answers
 *    'none'. The server cannot remember a "no".
 *  - There is no per-user "we asked you" column, and adding one would mean a
 *    prod migration against live clients for a prompt-suppression flag.
 * So a YES is remembered everywhere (the status turns 'confirmed' and no device
 * asks again), while a NO is remembered per device. Someone who declines on
 * their phone can be asked once more on their laptop. That is the price of not
 * migrating, and it is one dismissible card per device, ever.
 */

/** Dropped for the deleted user only - see useAuth's deleteAccount. */
export const ASKED_STORAGE_KEY = 'cb-email-prompt-asked'

/** Scratch key for the writability probe. Never read for meaning. */
const PROBE_KEY = 'cb-email-prompt-probe'

/**
 * The ids we have asked. There is deliberately NO expiry and NO cap.
 *
 * Both were here while only 24h-old accounts were asked, and both are wrong now
 * that everyone is asked once: an entry that expires is an account that gets
 * asked a second time, and a cap has to evict a live entry to make room. Growth
 * is bounded by how many accounts sign in on one browser - a handful of short
 * strings, permanently.
 *
 * null means storage itself is unusable, which is NOT an empty list: an answer
 * we cannot store is an answer we cannot honour, so callers read null as
 * "already asked" and stay quiet.
 */
function readAsked(): string[] | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(ASKED_STORAGE_KEY)
  } catch {
    // Storage disabled entirely (some private modes, "block all cookies").
    return null
  }
  if (!raw) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    // Corrupt VALUE, working storage. Start over rather than go silent: the
    // next write repairs it.
    return []
  }
  if (!Array.isArray(parsed)) return []
  return parsed.filter((v): v is string => typeof v === 'string')
}

function writeAsked(ids: string[]): void {
  try {
    localStorage.setItem(ASKED_STORAGE_KEY, JSON.stringify(ids))
  } catch {
    // Nothing to do, and nothing is lost: canRemember() is checked before the
    // card ever opens, so a storage that cannot be written never gets here.
  }
}

/**
 * Can this browser actually hold an answer? Reads are not enough: quota and
 * some storage policies allow getItem while refusing setItem, and a decline we
 * cannot persist brings the card back on the next load. Probed on a scratch
 * key so a full-quota failure cannot damage the real record.
 */
export function canRemember(): boolean {
  try {
    localStorage.setItem(PROBE_KEY, '1')
    const readBack = localStorage.getItem(PROBE_KEY)
    localStorage.removeItem(PROBE_KEY)
    return readBack === '1'
  } catch {
    return false
  }
}

/** True also when storage is unusable: an unrememberable answer is not asked. */
export function wasAsked(userId: string): boolean {
  const ids = readAsked()
  if (ids === null) return true
  return ids.includes(userId)
}

export function markAsked(userId: string): void {
  const ids = readAsked()
  if (ids === null || ids.includes(userId)) return
  writeAsked([...ids, userId])
}

/**
 * Drops one account's record and leaves every other account's alone. Account
 * deletion used to wipe the whole key, which re-exposed any OTHER account on
 * the same browser to a second prompt.
 */
export function forgetAsked(userId: string): void {
  const ids = readAsked()
  if (ids === null || !ids.includes(userId)) return
  writeAsked(ids.filter(id => id !== userId))
}
